import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Clock, ShieldCheck } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { ChapterTag, Marquee, Reveal, SpotlightCard } from "../components/Motion";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { LinkPills, Loading } from "../components/Sections";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function InsightDetail() {
  const { slug } = useParams();
  const { data, loading, error } = useApi(() => api.insight(slug), [slug]);

  if (loading) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="insight-not-found">
          <p className="font-display font-black uppercase text-4xl text-white">Article not found</p>
          <Link to="/insights" className="font-mono text-xs uppercase tracking-widest text-amber border border-amber px-5 py-3">
            All insights
          </Link>
        </div>
      </PageShell>
    );
  }

  const origin = window.location.origin;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.title,
    description: data.description,
    datePublished: data.date,
    author: { "@type": "Organization", name: "Xtreme Protection" },
    publisher: { "@type": "Organization", name: "Xtreme Protection" },
    keywords: data.keywords.join(", "),
  };

  return (
    <PageShell>
      <Seo title={data.page_title} description={data.description} path={`/insights/${slug}`} schema={articleSchema} image={data.image} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(data.breadcrumb, origin))}</script>

      <article className="relative tactical-grid border-b border-white/10" data-testid="insight-article">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink to-ink pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12 pb-12">
          <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-amber transition-colors">Home</Link>
            <span className="text-amber">//</span>
            <Link to="/insights" className="hover:text-amber transition-colors">Insights</Link>
            <span className="text-amber">//</span>
            <span className="text-amber">{data.category}</span>
          </nav>
          <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-6">
            <span className="text-amber border border-amber/40 px-2.5 py-1">{data.category}</span>
            <span>{fmtDate(data.date)}</span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> {data.minutes} min read
            </span>
            <span>Xtreme Protection Intelligence Desk</span>
          </div>
          <h1 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-5xl text-white" data-testid="insight-title">
            {data.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-gray-300 leading-relaxed">{data.lead}</p>
        </div>
      </article>

      <div className="relative h-64 sm:h-96 overflow-hidden border-b border-white/10" data-testid="insight-cover">
        <img src={data.image} alt={data.title} className="w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
      </div>

      <Marquee items={data.keywords.map((k) => k.replace(/\b\w/g, (c) => c.toUpperCase()))} />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12" data-testid="insight-body">
        {data.sections.slice(0, 2).map((sec, i) => (
          <Reveal key={i} className="mb-10">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white mb-4">{sec.heading}</h2>
            {sec.paragraphs.map((p, j) => (
              <p key={j} className="text-sm sm:text-base text-gray-400 leading-relaxed mb-4">{p}</p>
            ))}
          </Reveal>
        ))}

        <Reveal className="my-12 border-l-2 border-amber pl-6 sm:pl-8" data-testid="insight-quote">
          <p className="font-display font-bold uppercase tracking-wide text-xl sm:text-2xl text-amber leading-snug">
            “{data.quote}”
          </p>
        </Reveal>

        {data.sections.slice(2).map((sec, i) => (
          <Reveal key={i} className="mb-10">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white mb-4">{sec.heading}</h2>
            {sec.paragraphs.map((p, j) => (
              <p key={j} className="text-sm sm:text-base text-gray-400 leading-relaxed mb-4">{p}</p>
            ))}
          </Reveal>
        ))}
      </div>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-12" data-testid="insight-related-services">
        <ChapterTag num="OPS" title="RELATED SECURITY SERVICES" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {data.related_services.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`insight-service-${s.slug}`} className="block group">
              <SpotlightCard className="p-5 h-full hover:border-amber/60 transition-colors duration-300">
                <ShieldCheck className="w-4 h-4 text-amber mb-3" />
                <span className="block font-display font-bold uppercase tracking-wide text-base text-white group-hover:text-amber transition-colors leading-tight">
                  {s.name}
                </span>
                <span className="block mt-2 text-xs text-gray-400 leading-relaxed">{s.short}</span>
              </SpotlightCard>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="insight-related-articles">
        <ChapterTag num="NEXT" title="MORE FROM THE INTELLIGENCE DESK" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.related_articles.map((a) => (
            <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`insight-related-${a.slug}`} className="block group border border-white/10 bg-card p-6 hover:border-amber transition-colors duration-300">
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                <span className="text-amber">{a.category}</span>
                <span>{fmtDate(a.date)}</span>
              </div>
              <h3 className="mt-3 font-display font-bold uppercase tracking-wide text-lg text-white group-hover:text-amber transition-colors leading-tight">
                {a.title}
              </h3>
              <ArrowUpRight className="mt-4 w-4 h-4 text-amber group-hover:rotate-45 transition-transform duration-300" />
            </Link>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
